import { useState, useMemo, useRef } from "react";
import {
  Select,
  InputNumber,
  Typography,
  Row,
  Col,
  Grid,
  Tabs,
  Space,
  Empty,
  Spin,
  Button,
  Radio,
} from "antd";
import {
  CarOutlined,
  DollarOutlined,
  BankOutlined,
  UserOutlined,
  FileTextOutlined,
  SwapOutlined,
  CalculatorOutlined,
} from "@ant-design/icons";
import type { FmvAPI } from "../fmv/FmvTableComponent";
import type { ProvidersData } from "../../helpers/interfaces/ProvidersInterfaces";
import { CustomerViewCard } from "./CustomerViewCard";
import { PartnerViewTable } from "./PartnerViewTable";
import { searchVehiclesByFMV } from "../../api/VehiclesApi";
import { useProviderCalculation } from "../../hooks/calculator/useProviderCalculation";

const { Text, Title } = Typography;
const { Option } = Select;

type UnitType = "ACV" | "Repoz";

type CalculatorIndexComponentProps = {
  providers: ProvidersData[];
  providersLoading: boolean;
};

const TERM_LABELS: Record<number, string> = {
  12: "1 Year",
  24: "2 Years",
  36: "3 Years",
  48: "4 Years",
  60: "5 Years",
  72: "6 Years",
  84: "7 Years",
};

const labelFromMonths = (months: number) =>
  TERM_LABELS[months] || `${months} Months`;

/**
 * Check if a provider is TFS based on its name.
 */
const isTfsProvider = (providerName: string): boolean => {
  return providerName.toLowerCase().includes("tfs");
};

export const CalculatorIndexComponent = ({
  providers,
  providersLoading,
}: CalculatorIndexComponentProps) => {
  const screens = Grid.useBreakpoint();

  // ── Provider Calculation Hook ──
  const {
    calculationResult,
    loading: calcLoading,
    monthlyTerms,
    selectedTerm,
    selectedTermIndex: apiSelectedTermIndex,
    calculate,
    reset: resetCalculation,
    setSelectedTermIndex: setApiSelectedTermIndex,
    isDirty,
  } = useProviderCalculation();

  // ── Input State ──
  const [selectedVehicleBMTYTL, setSelectedVehicleBMTYTL] = useState<string | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<FmvAPI | null>(null);
  const [searchOptions, setSearchOptions] = useState<FmvAPI[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [unitType, setUnitType] = useState<UnitType>("Repoz");
  const [selectedProviderId, setSelectedProviderId] = useState<number | null>(null);
  const [unitPrice, setUnitPrice] = useState<number>(0);
  const [downpayment, setDownpayment] = useState<number>(0);
  const [dpMode, setDpMode] = useState<"amount" | "percent">("percent");
  const [dpPercent, setDpPercent] = useState<number>(10);
  const [selectedTermIndex, setSelectedTermIndex] = useState<number>(0);

  // ── TFS-specific: manual make input ──
  const [manualMake, setManualMake] = useState<string>("");

  const activeProviders = useMemo(
    () => providers.filter((p) => p.is_active === 1),
    [providers]
  );

  const selectedProvider = useMemo(
    () => providers.find((p) => p.provider_id === selectedProviderId) || null,
    [providers, selectedProviderId]
  );

  const isTfs = useMemo(
    () => (selectedProvider ? isTfsProvider(selectedProvider.provider_name) : false),
    [selectedProvider]
  );

  const financedAmount = useMemo(
    () => Math.max(unitPrice - downpayment, 0),
    [unitPrice, downpayment]
  );

  const downpaymentPercent = useMemo(
    () => (unitPrice > 0 ? (downpayment / unitPrice) * 100 : 0),
    [unitPrice, downpayment]
  );

  // ── Handlers ──
  const handleSearch = (value: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!value) {
      setSearchOptions([]);
      return;
    }

    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await searchVehiclesByFMV(value);
        const data = res?.data?.data || res?.data || [];
        if (Array.isArray(data)) {
          setSearchOptions(data);
        }
      } catch (error) {
        console.error("Vehicle search error:", error);
      } finally {
        setIsSearching(false);
      }
    }, 500);
  };

  const handleVehicleChange = (bmtytl: string) => {
    setSelectedVehicleBMTYTL(bmtytl);

    const searchedVehicle = searchOptions.find((v) => v.bmtytl_code === bmtytl);

    if (searchedVehicle) {
      setSelectedVehicle(searchedVehicle);
    }

    setSelectedTermIndex(0);
    resetCalculation();
  };

  const handleUnitPriceChange = (value: number | null) => {
    const price = value || 0;
    setUnitPrice(price);
    if (dpMode === "percent") {
      setDownpayment(Math.round((price * dpPercent) / 100));
    }
    resetCalculation();
  };

  const handleDownpaymentChange = (value: number | null) => {
    const dp = value || 0;
    setDownpayment(dp);
    setDpMode("amount");
    // Sync the percentage input to match
    if (unitPrice > 0) {
      setDpPercent(parseFloat(((dp / unitPrice) * 100).toFixed(2)));
    }
    resetCalculation();
  };

  const handleDpPercentChange = (value: number | null) => {
    const pct = value || 0;
    setDpPercent(pct);
    setDownpayment(Math.round((unitPrice * pct) / 100));
    setDpMode("percent");
    resetCalculation();
  };

  const handleUnitTypeChange = (type: UnitType) => {
    setUnitType(type);
    resetCalculation();
  };

  const handleProviderChange = (val: number) => {
    setSelectedProviderId(val);
    setSelectedTermIndex(0);
    resetCalculation();
  };

  // ── Calculate Button ──
  const handleCalculate = async () => {
    if (!selectedProvider) return;
    if (unitPrice <= 0 || downpayment <= 0) return;

    const make = isTfs
      ? manualMake || "toyota"
      : selectedVehicle?.brand || "toyota";

    const payload: any = {
      unit_price: unitPrice,
      provider_name: selectedProvider.provider_name.toLowerCase(),
      dp_amount: downpayment,
      make: make.toLowerCase(),
      is_acv: unitType === "ACV" ? 1 : 0,
      is_repoz: unitType === "Repoz" ? 1 : 0,
      is_include_for_partners: 1,
    };

    // Include bmtytl_code if NOT tfs
    if (!isTfs && selectedVehicle?.bmtytl_code) {
      payload.bmtytl_code = selectedVehicle.bmtytl_code;
    }

    await calculate(payload);
  };

  const canCalculate = useMemo(() => {
    if (!selectedProvider) return false;
    if (unitPrice <= 0) return false;
    if (downpayment <= 0) return false;
    // Non-TFS requires a selected vehicle
    if (!isTfs && !selectedVehicle) return false;
    return true;
  }, [selectedProvider, unitPrice, downpayment, isTfs, selectedVehicle]);

  // ── Build customer-view term results from API response ──
  const customerTermResults = useMemo(() => {
    if (!monthlyTerms || monthlyTerms.length === 0) return [];

    return monthlyTerms.map((t) => ({
      label: labelFromMonths(t.months_to_pay),
      months: t.months_to_pay,
      interestRate: t.interest_rate,
      monthlyPayment: t.monthly_amount,
      totalInterest: 0,
      totalPayable: 0,
    }));
  }, [monthlyTerms]);

  // ── Styles ──
  const sectionCardStyle = {
    background: "#ffffff",
    border: "1px solid #d6dee8",
    borderRadius: "12px",
    padding: "24px",
    marginBottom: "20px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.03)",
  };

  const sectionHeaderStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "15px",
    fontWeight: 600,
    color: "#1e293b",
    marginBottom: "20px",
  };

  return (
    <div style={{ margin: "0 auto" }}>
      <Row gutter={[24, 24]}>
        {/* ── LEFT PANEL: Inputs ── */}
        <Col xs={24} lg={10} xl={9}>
          {/* Unit Type Selection — Radio Buttons */}
          <div style={sectionCardStyle}>
            <div style={sectionHeaderStyle}>
              <SwapOutlined style={{ color: "#334155" }} />
              Unit Type
            </div>
            <Radio.Group
              value={unitType}
              onChange={(e) => handleUnitTypeChange(e.target.value)}
              style={{ width: "100%" }}
            >
              <Space direction="horizontal" size={16} style={{ width: "100%" }}>
                <Radio.Button
                  value="ACV"
                  style={{
                    flex: 1,
                    textAlign: "center",
                    height: "auto",
                    padding: "12px 24px",
                    borderRadius: "8px",
                    fontWeight: unitType === "ACV" ? 700 : 400,
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "14px" }}>ACV</div>
                    <div style={{ fontSize: "11px", color: "#64748b" }}>
                      Automart Certified
                    </div>
                  </div>
                </Radio.Button>
                <Radio.Button
                  value="Repoz"
                  style={{
                    flex: 1,
                    textAlign: "center",
                    height: "auto",
                    padding: "12px 24px",
                    borderRadius: "8px",
                    fontWeight: unitType === "Repoz" ? 700 : 400,
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "14px" }}>Repoz</div>
                    <div style={{ fontSize: "11px", color: "#64748b" }}>
                      Repossessed Unit
                    </div>
                  </div>
                </Radio.Button>
              </Space>
            </Radio.Group>
          </div>

          {/* Provider Selection — Moved up so TFS detection drives vehicle section */}
          <div style={sectionCardStyle}>
            <div style={sectionHeaderStyle}>
              <BankOutlined style={{ color: "#334155" }} />
              Financing Provider
            </div>
            <Select
              showSearch
              placeholder="Select a provider..."
              style={{ width: "100%" }}
              size="large"
              value={selectedProviderId}
              onChange={handleProviderChange}
              loading={providersLoading}
              filterOption={(input, option) =>
                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {activeProviders.map((p) => (
                <Option key={p.provider_id} value={p.provider_id}>
                  {p.provider_name}
                </Option>
              ))}
            </Select>

            {selectedProvider && (
              <div
                style={{
                  marginTop: "12px",
                  padding: "8px 12px",
                  background: isTfs ? "#e6f7ff" : "#f0f2ff",
                  borderRadius: "6px",
                  borderLeft: `3px solid ${isTfs ? "#f97316" : "#334155"}`,
                }}
              >
                <Text style={{ fontSize: "12px", color: isTfs ? "#f97316" : "#334155" }}>
                  {isTfs
                    ? "TFS Provider — Vehicle selection is optional. You can enter price & DP directly."
                    : "Non-TFS Provider — Please select a vehicle from the BMTYTL list below."}
                </Text>
              </div>
            )}
          </div>

          {/* Vehicle Selection — shown only for Non-TFS */}
          {!isTfs && (
            <div style={sectionCardStyle}>
              <div style={sectionHeaderStyle}>
                <CarOutlined style={{ color: "#334155" }} />
                Vehicle Selection (BMTYTL)
              </div>
              <Select
                showSearch
                placeholder="Search by Brand, Model, or BMTYTL..."
                style={{ width: "100%" }}
                size="large"
                value={selectedVehicleBMTYTL}
                onChange={handleVehicleChange}
                onSearch={handleSearch}
                filterOption={false}
                loading={isSearching}
                notFoundContent={isSearching ? <Spin size="small" /> : <Empty description="No vehicles found" />}
              >
                {searchOptions.map((v) => (
                  <Option key={v.vehicle_id} value={v.bmtytl_code}>
                    {`${v.transmission} ${v.brand} ${v.model} ${v.trim} (${v.model_year}) - ${v.location}`}
                  </Option>
                ))}
              </Select>

              {selectedVehicle && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "12px 16px",
                    background: "#f8f9fb",
                    borderRadius: "8px",
                    borderLeft: `4px solid ${unitType === "ACV" ? "#334155" : "#d4380d"}`,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <Text type="secondary" style={{ fontSize: "12px", display: "block", marginBottom: "4px" }}>
                        Selected Vehicle
                      </Text>
                      <Text strong style={{ fontSize: "14px", color: "#1e293b" }}>
                        {selectedVehicle.brand} {selectedVehicle.model} {selectedVehicle.trim}
                      </Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        {selectedVehicle.model_year} · {selectedVehicle.transmission} · {selectedVehicle.location}
                      </Text>
                      <br />
                      <Text code style={{ fontSize: "11px", marginTop: "4px" }}>
                        {selectedVehicle.bmtytl_code}
                      </Text>
                    </div>
                    <div
                      style={{
                        padding: "2px 10px",
                        borderRadius: "4px",
                        fontSize: "11px",
                        fontWeight: 700,
                        background: unitType === "ACV" ? "#f0f2ff" : "#fff2e8",
                        color: unitType === "ACV" ? "#334155" : "#d4380d",
                      }}
                    >
                      {unitType}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pricing Inputs */}
          <div style={sectionCardStyle}>
            <div style={sectionHeaderStyle}>
              <DollarOutlined style={{ color: "#334155" }} />
              Pricing Configuration
            </div>

            {/* TFS: optional make input */}
            {isTfs && (
              <div style={{ marginBottom: "20px" }}>
                <Text style={{ fontSize: "13px", fontWeight: 500, display: "block", marginBottom: "8px" }}>
                  Car Make (Brand)
                </Text>
                <Select
                  showSearch
                  placeholder="e.g. Toyota, Honda, Mitsubishi..."
                  style={{ width: "100%" }}
                  size="large"
                  value={manualMake || undefined}
                  onChange={(val) => setManualMake(val)}
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {["Toyota", "Honda", "Mitsubishi", "Nissan", "Suzuki", "Ford", "Hyundai", "Kia", "Mazda", "Isuzu", "Chevrolet", "Subaru", "BMW", "Mercedes-Benz", "Audi", "Volkswagen"].map(
                    (make) => (
                      <Option key={make} value={make.toLowerCase()}>
                        {make}
                      </Option>
                    )
                  )}
                </Select>
              </div>
            )}

            <div style={{ marginBottom: "20px" }}>
              <Text style={{ fontSize: "13px", fontWeight: 500, display: "block", marginBottom: "8px" }}>
                Unit Price / SRP (₱)
              </Text>
              <InputNumber
                size="large"
                style={{ width: "100%", borderRadius: "6px" }}
                value={unitPrice}
                onChange={handleUnitPriceChange}
                min={0}
                step={1000}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(value) => value!.replace(/,/g, "") as unknown as number}
                prefix="₱"
              />
              {!isTfs && selectedVehicle && (
                <div style={{ marginTop: "6px" }}>
                  <Space size={4} wrap>
                    {[
                      { label: "TFS Fin", value: selectedVehicle.tfs_fin_p },
                      { label: "GDFI Fin", value: selectedVehicle.gdfi_fin_p },
                      { label: "Cash", value: selectedVehicle.discounted_cash_price },
                    ]
                      .filter((btn) => btn.value)
                      .map((btn) => (
                        <Text
                          key={btn.label}
                          onClick={() => handleUnitPriceChange(btn.value)}
                          style={{
                            fontSize: "11px",
                            color: "#334155",
                            cursor: "pointer",
                            background: "#f0f2ff",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            fontWeight: 500,
                          }}
                        >
                          {btn.label}: ₱{btn.value?.toLocaleString("en-PH")}
                        </Text>
                      ))}
                  </Space>
                </div>
              )}
            </div>

            <Row gutter={12}>
              <Col span={14}>
                <Text style={{ fontSize: "13px", fontWeight: 500, display: "block", marginBottom: "8px" }}>
                  Downpayment (₱)
                </Text>
                <InputNumber
                  size="large"
                  style={{ width: "100%", borderRadius: "6px" }}
                  value={downpayment}
                  onChange={handleDownpaymentChange}
                  min={0}
                  step={1000}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => value!.replace(/,/g, "") as unknown as number}
                  prefix="₱"
                />
              </Col>
              <Col span={10}>
                <Text style={{ fontSize: "13px", fontWeight: 500, display: "block", marginBottom: "8px" }}>
                  DP Percentage (%)
                </Text>
                <InputNumber
                  size="large"
                  style={{ width: "100%", borderRadius: "6px" }}
                  value={dpPercent}
                  onChange={handleDpPercentChange}
                  min={0}
                  max={100}
                  step={1}
                />
              </Col>
            </Row>

            {financedAmount > 0 && (
              <div
                style={{
                  marginTop: "16px",
                  padding: "12px 16px",
                  background: "linear-gradient(135deg, #f0f2ff 0%, #e8eaff 100%)",
                  borderRadius: "8px",
                  textAlign: "center",
                }}
              >
                <Text type="secondary" style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Financed Amount
                </Text>
                <Title level={4} style={{ margin: "4px 0 0", color: "#1e293b" }}>
                  ₱{financedAmount.toLocaleString("en-PH")}
                </Title>
              </div>
            )}
          </div>

          {/* Calculate Button */}
          <div style={sectionCardStyle}>
            <Button
              type="primary"
              size="large"
              block
              icon={<CalculatorOutlined />}
              loading={calcLoading}
              disabled={!canCalculate}
              onClick={handleCalculate}
              style={{
                height: "52px",
                fontSize: "16px",
                fontWeight: 700,
                borderRadius: "10px",
                background: canCalculate
                  ? "linear-gradient(135deg, #1e293b 0%, #334155 100%)"
                  : undefined,
                border: "none",
              }}
            >
              {calcLoading ? "Calculating..." : "Calculate"}
            </Button>
          </div>
        </Col>

        {/* ── RIGHT PANEL: Results ── */}
        <Col xs={24} lg={14} xl={15}>
          <Tabs
            defaultActiveKey="customer"
            type="card"
            size="large"
            tabBarStyle={{
              marginBottom: "0",
            }}
            items={[
              {
                key: "customer",
                label: (
                  <span>
                    <UserOutlined /> Customer View
                  </span>
                ),
                children: (
                  <div style={{ paddingTop: "20px" }}>
                    <CustomerViewCard
                      unitPrice={unitPrice}
                      downpayment={downpayment}
                      terms={customerTermResults}
                      selectedTermIndex={apiSelectedTermIndex}
                      onSelectTerm={setApiSelectedTermIndex}
                      isDirty={isDirty}
                    />
                  </div>
                ),
              },
              {
                key: "partner",
                label: (
                  <span>
                    <FileTextOutlined /> Partner View
                  </span>
                ),
                children: (
                  <div style={{ paddingTop: "20px" }}>
                    <PartnerViewTable
                      unitType={unitType}
                      unitPrice={unitPrice}
                      downpayment={downpayment}
                      downpaymentPercent={downpaymentPercent}
                      financedAmount={financedAmount}
                      providerName={selectedProvider?.provider_name || "—"}
                      vehicle={selectedVehicle}
                      isTfs={isTfs}
                      calculationResult={calculationResult}
                      monthlyTerms={monthlyTerms}
                      selectedTermIndex={apiSelectedTermIndex}
                      onSelectTerm={setApiSelectedTermIndex}
                      isDirty={isDirty}
                    />
                  </div>
                ),
              },
            ]}
          />
        </Col>
      </Row>
    </div>
  );
};
